import {
  drag,
  forceCenter,
  forceLink,
  forceManyBody,
  forceSimulation,
  select,
  Simulation,
  SimulationNodeDatum,
} from "d3";
import { isEqual } from "lodash";
import { add, atan, distance, matrix, multiply } from "mathjs";
import { Component, createRef, RefObject } from "react";
import { createPortal } from "react-dom";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { Option } from "space-monad";
import { VerificationDTO } from "../../../../../../store/verification/models/dto/due-diligence";
import { DueDiligenceKycType } from "../../../../../../store/verification/models/internal";
import { colours } from "../../../../../../styles/colours";
import { StyledWidget } from "../common";
import { isCompanyNode, isKycNode, KycNode, Link, Node } from "./common";
import { Tooltip } from "./tooltip";
import "./tooltip.scss";

interface IChartProps {
  kycs: VerificationDTO.Responses.DueDiligence.Form.Get.T["kycs"];
  participants: VerificationDTO.Responses.DueDiligence.Form.Get.T["participants"];
  kyb: Exclude<
    VerificationDTO.Responses.DueDiligence.Form.Get.T["kyb"],
    null | undefined
  >;
  dueDiligenceTypes: DueDiligenceKycType;
}

// user is draggging
let dragging = false;

class Chart extends Component<
  IChartProps,
  {
    selectedNode?: Node;
    tooltipElem?: any;
  }
> {
  ref: RefObject<HTMLDivElement>;
  graphData: any = undefined;

  constructor(props: IChartProps) {
    super(props);
    this.ref = createRef<HTMLDivElement>();
  }

  state = {
    tooltipElem: undefined,
    selectedNode: undefined,
  };

  setTooltip = (n: Node) => {
    this.setState({
      selectedNode: n,
    });
  };

  updateGraph() {
    const { kycs, participants, kyb } = this.props;
    this.graphData = {
      nodes: [
        ...kycs.map((x) => ({
          id: `${x.details.user.firstName} ${x.details.user.lastName}`,
          fraudCheckId: x.details.fraudCheckId,
          types: x.types,
          status: x.details.userStatus,
        })),
        ...participants
          .filter(
            (x) =>
              !kycs.find((y) => y.details.user.emailAddress === x.emailAddress)
          )
          .map((x) => ({
            id: `${x.firstName} ${x.lastName}`,
          })),
        {
          id: kyb.companyName,
          companyRegNumber: kyb.taxIdentificationNo,
          companyRegCountry: kyb.taxCountry,
        },
      ],
      links: [
        ...kycs.map((x) => ({
          source: `${x.details.user.firstName} ${x.details.user.lastName}`,
          target: kyb.companyName,
        })),
        ...participants.map((x) => ({
          source: `${x.firstName} ${x.lastName}`,
          target: kyb.companyName,
        })),
      ],
    };

    if (this.ref.current) {
      const { tooltip } = forceGraph(this.graphData, {
        ref: this.ref.current,
        setTooltip: this.setTooltip,
        dueDiligenceTypes: this.props.dueDiligenceTypes,
        //   invalidation, // a promise to stop the simulation when the cell is re-run
      });
      this.setState({
        tooltipElem: tooltip,
      });
    }
  }

  resizeListener = () => {
    this.updateGraph();
  };

  resizeSubject = new Subject<void>();

  resizeSub?: Subscription;

  resizeHandler = () => {
    this.resizeSubject.next();
  };

  componentDidMount() {
    this.updateGraph();
    window.addEventListener("resize", this.resizeHandler);

    this.resizeSub = this.resizeSubject
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.updateGraph();
      });
  }

  componentWillUnmount() {
    if (this.resizeSub) {
      this.resizeSub.unsubscribe();
    }
    window.removeEventListener("resize", this.resizeHandler);
  }

  componentDidUpdate(prev: IChartProps) {
    if (!isEqual(prev, this.props)) this.updateGraph();
  }

  render() {
    return (
      <StyledWidget title="Charts">
        <div ref={this.ref} />
        {this.state.tooltipElem && this.state.selectedNode
          ? createPortal(
              <Tooltip node={this.state.selectedNode} />,
              this.state.tooltipElem
            )
          : null}
      </StyledWidget>
    );
  }
}

export default Chart;

const forceGraph = (
  {
    nodes, // an iterable of node objects (typically [{id}, …])
    links, // an iterable of link objects (typically [{source, target}, …])
  }: {
    nodes: Array<Node>;
    links: Array<Link>;
  },
  {
    ref,
    setTooltip,
    dueDiligenceTypes,
  }: {
    ref?: any;
    setTooltip: (n: Node) => void;
    dueDiligenceTypes: DueDiligenceKycType;
  }
) => {
  function dragHandler(
    simulation2: Simulation<SimulationNodeDatum, undefined>
  ) {
    let tooltipOpen: boolean = false;
    function dragstarted(event: any) {
      if (tooltip.style("visibility") === "visible") {
        tooltipOpen = true;
        tooltip.style("visibility", "hidden");
      }
      dragging = true;
      if (!event.active) simulation2.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event: any) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event: any) {
      if (tooltipOpen) {
        tooltipOpen = false;
      }
      dragging = false;
      if (!event.active) simulation2.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    return drag()
      .on("start", dragstarted)
      .on("drag", dragged)
      .on("end", dragended);
  }

  const simulation = forceSimulation(
    (nodes as any) as Array<SimulationNodeDatum>
  )
    .force("link", forceLink())
    .force("charge", forceManyBody().strength(-60))
    .force("center", forceCenter())
    .on("tick", ticked);

  select(ref).selectChildren("svg").remove();

  const svg = select(ref)
    .append("svg")
    .attr("viewBox", [-ref.clientWidth / 2, -200, ref.clientWidth, 400])
    .attr("style", "max-width: 100%; height: 400; height: intrinsic;");

  const link = svg.append("g").selectAll("line").data(links).join("polygon");
  const linkText = svg.append("g").selectAll("line").data(links).join("g");

  const rect = linkText
    .append("rect")
    .attr("fill", "#333")
    .attr("rx", 7)
    .attr("ry", 7);

  linkText
    .append("text")
    .style("fill", "#ffffff")
    .style("font-weight", 500)
    .style("text-transform", "uppercase")
    .text((x) => {
      const node = nodes.find((y) => y.id === x.source);
      if (node) {
        if (isKycNode(node)) {
          return node.types.map((s) => dueDiligenceTypes.enum[s]).join(" / ");
        }
      }
      return "PENDING";
    });

  // Construct the scales.
  const foo = dragHandler(simulation) as any;
  const container = select(ref).style("position", "relative");
  const tooltip = container.append("div").attr("class", "tooltip");
  const circleGroup = svg.append("g");
  const node = circleGroup
    .selectAll("circle")
    .data(nodes)
    .join(
      (enter) => {
        const g: any = enter.append("g");

        g.append("text")
          .text((x: any) => x.id)
          .attr("x", (x: Node) => (isCompanyNode(x) ? 50 : 30))
          .attr("y", "0.5em");

        g.append("circle")
          .attr("stroke", "#444")
          .attr("stroke-opacity", 0)
          .attr("stroke-width", 1.5)
          .attr("fill", (x: Node) =>
            isCompanyNode(x) ? colours.primaryBlue : colours.blue
          )
          .attr("cx", 0)
          .attr("cy", 0)
          .style("opacity", 0)
          .on("mouseenter", function (x: any, node: Node) {
            if (dragging === false) {
              select(this).attr("stroke-opacity", 1);
              tooltip
                .style("visibility", "visible")
                .style("top", `${x.pageY}px`)
                .style("left", `${x.pageX}px`)
                .on("mouseenter", function (e) {
                  tooltip.style("visibility", "visible");
                })
                .on("mouseleave", function (e) {
                  tooltip.style("visibility", "hidden");
                });
              setTooltip(node);
            }
          })
          .on("mouseleave", function (e: any) {
            select(this).attr("stroke-opacity", 0);
            tooltip.style("visibility", "hidden");
          })
          .call((e: any) =>
            e
              .transition()
              .duration(1000)
              .attr("r", (x: Node) => (isCompanyNode(x) ? 40 : 25))
              .style("opacity", 1)
          );
        return g;
      },
      (update) => update,
      (exit) => exit.call((e) => e.transition().remove())
    )
    .each(function (datum) {
      // If the node is the company add tower
      if (isCompanyNode(datum)) {
        select(this)
          .append("g")
          .html(
            `
              <path d="M247.132,100.872V51.25h-59.375V0h-15v51.25h-59.375v49.622H89.196v259.64h182.121v-259.64    C271.317,100.872,247.132,100.872,247.132,100.872z M128.382,66.25h103.75v34.622h-103.75V66.25z M256.317,345.512H104.196    v-229.64h9.186h133.75h9.186L256.317,345.512L256.317,345.512z"/>
              <rect x="113.382" y="142.756" width="15" height="33.75"/>
              <rect x="152.756" y="142.756" width="15" height="33.75"/>
              <rect x="192.13" y="142.756" width="15" height="33.75"/>
              <rect x="231.504" y="142.756" width="15" height="33.75"/>
              <rect x="113.382" y="201.923" width="15" height="33.75"/>
              <rect x="152.756" y="201.923" width="15" height="33.75"/>
              <rect x="192.13" y="201.923" width="15" height="33.75"/>
              <rect x="231.504" y="201.923" width="15" height="33.75"/>
              <rect x="113.382" y="261.089" width="15" height="33.75"/>
              <rect x="152.756" y="261.089" width="15" height="33.75"/>
              <rect x="192.13" y="261.089" width="15" height="33.75"/>
              <rect x="231.504" y="261.089" width="15" height="33.75"/>`
          )
          .attr("fill", "white")
          .style(
            "transform",
            (x: any) => `scale(0.14) translate(-171px, -190px)`
          )
          .style("pointer-events", "none");
      } else {
        // else add user icon
        select(this)
          .append("path")
          .attr("fill", "white")
          .attr(
            "d",
            "M14.5 2A12.514 12.514 0 0 0 2 14.5 12.521 12.521 0 0 0 14.5 27a12.5 12.5 0 0 0 0-25Zm7.603 19.713a8.48 8.48 0 0 0-15.199.008A10.367 10.367 0 0 1 4 14.5a10.5 10.5 0 0 1 21 0 10.368 10.368 0 0 1-2.897 7.213ZM14.5 7a4.5 4.5 0 1 0 4.5 4.5A4.5 4.5 0 0 0 14.5 7Z"
          )
          .style(
            "transform",
            (x: any) =>
              `scale(${
                x.group === 0 ? 1.3 : 0.8
              }) translate(-${14.5}px,-${14.5}px)`
          )
          .style("pointer-events", "none");
      }
    })
    .call(foo);

  function ticked() {
    const nodes = node.data();
    link.attr("fill", "#ddd").attr("points", (d: any) => {
      const source = Option(nodes as any[])
        .map((x) => x.find(({ id }) => d.source === id))
        .map(({ x, y }) => [x, y]);
      const target = Option(nodes as any[])
        .map((x) => x.find(({ id }) => d.target === id))
        .map(({ x, y }) => [x, y]);

      const length = Option.all([source, target]).map(([s, t]) =>
        distance(s, t)
      );

      const triangle = length.map((l) => [
        [0, 10],
        [l, 0],
        [0, -10],
      ]);

      const rotation = Option.all([source, target]).map(([s, t]) => {
        const negativeX = s[0] - t[0] < 0;
        const theta = atan((s[1] - t[1]) / (s[0] - t[0]));
        return negativeX ? theta + Math.PI : theta;
      });

      const rotationM = rotation.map((r) =>
        matrix([
          [Math.cos(-r), -Math.sin(-r)],
          [Math.sin(-r), Math.cos(-r)],
        ])
      );
      const translationM = target.map((t) => matrix(t));

      const translatedPoints = Option.all([
        rotationM,
        translationM,
        triangle,
      ]).map(([rM, tM, tri]) =>
        tri.map((p) => multiply(p, rM)).map((p) => add(p, tM))
      );

      return translatedPoints.fold(
        () => "",
        (translated) => translated.map((x) => x.toArray().join(",")).join(" ")
      );
    });

    // Adjust background
    rect.attr("x", () => -10);
    rect.attr(
      "width",
      (z, index, elems) =>
        (elems[index]?.nextSibling as any)?.getBoundingClientRect().width +
          20 || 0
    );

    rect.attr(
      "y",
      (z, index, elems) =>
        -(
          (elems[index]?.nextSibling as any)?.getBoundingClientRect().height + 5
        ) || 0
    );

    rect.attr(
      "height",
      (z, index, elems) =>
        (elems[index]?.nextSibling as any)?.getBoundingClientRect().height +
          15 || 0
    );

    // Adjust label
    linkText.style("transform", (d: any, index, elems) => {
      const source = Option(nodes as any[])
        .map((x) => x.find(({ id }) => d.source === id))
        .map(({ x, y }) => [x, y]);

      const target = Option(nodes as any[])
        .map((x) => x.find(({ id }) => d.target === id))
        .map(({ x, y }) => [x, y]);

      const labelWidth = (elems[index] as any)?.getBoundingClientRect().width;
      return Option.all([source, target])
        .map(
          ([s, t]: any) =>
            `translate(${(s[0] - t[0]) / 2 + t[0] - labelWidth / 2}px, ${
              (s[1] - t[1]) / 2 + t[1]
            }px)`
        )
        .getOrElse("");
    });

    node
      .style("transform", (d: any) => `translate(${d.x}px, ${d.y}px)`)
      .style("color", "blue");
  }

  return {
    svgNode: Object.assign(svg.node() as any),
    tooltip: tooltip.node(),
  };
};
